import { useEffect, useRef, useState } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Mic, Settings, SmilePlus, Sparkles, Video, X } from "lucide-react"
import { BackgroundEffects } from "./BackgroundEffects"
import { DeviceSettingsMenu } from "./DeviceSettingsMenu"
import { GeneralPreferencesSettings, ReactionPreferencesSettings } from "./UserPreferencesSettings"
import { VideoQualitySettings } from "./VideoQualitySettings"

export type CallSettingsPane = "audio" | "video" | "general" | "effects" | "reactions"

const PANES: Array<{ id: CallSettingsPane; label: string; icon: React.ReactNode }> = [
  { id: "audio", label: "Audio", icon: <Mic className="h-4 w-4" /> },
  { id: "video", label: "Video", icon: <Video className="h-4 w-4" /> },
  { id: "general", label: "General", icon: <Settings className="h-4 w-4" /> },
  { id: "effects", label: "Effects", icon: <Sparkles className="h-4 w-4" /> },
  { id: "reactions", label: "Reactions", icon: <SmilePlus className="h-4 w-4" /> },
]

function settingsReturnFocusTarget(): HTMLElement | null {
  const active = document.activeElement
  if (!(active instanceof HTMLElement)) return null
  // Opening Settings removes its quick/More menu. Return to that menu's
  // persistent toolbar trigger instead of the now-detached settings item.
  for (const [menu, trigger] of [["call-cam-menu", "call-cam-options"], ["call-mic-menu", "call-mic-options"], ["call-more-menu", "call-more-options"]]) {
    if (active.closest(`[data-testid="${menu}"]`)) return document.querySelector<HTMLElement>(`[data-testid="${trigger}"]`) || active
  }
  return active
}

/**
 * In-call Settings dialog laid out like Google Meet's: a list of sections down the left
 * (Audio, Video, General, Effects, Reactions) and the chosen section's controls on the right.
 * Every pane stays mounted (hidden) so device lists and pending changes survive switching. The
 * dialog has a stable, viewport-bounded size so the section list does not move between panes.
 */
export function CallSettingsDialog({ onClose, onMuteMicrophone, initialPane = "audio", content }: {
  onClose: () => void
  onMuteMicrophone?: () => void
  initialPane?: CallSettingsPane
  /** Replaces the browser panes; only the given sections are listed (the mobile app's native call). */
  content?: Partial<Record<CallSettingsPane, React.ReactNode>>
}) {
  const panes = content ? PANES.filter((pane) => content[pane.id] !== undefined) : PANES
  const [activePane, setActivePane] = useState<CallSettingsPane>(panes.some((pane) => pane.id === initialPane) ? initialPane : panes[0].id)
  const [portalContainer, setPortalContainer] = useState<Element>(() => document.fullscreenElement || document.body)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [returnFocusTarget] = useState(settingsReturnFocusTarget)
  useEffect(() => {
    const updateContainer = () => setPortalContainer(document.fullscreenElement || document.body)
    document.addEventListener("fullscreenchange", updateContainer)
    return () => document.removeEventListener("fullscreenchange", updateContainer)
  }, [])
  return (
    <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose() }}>
      {/* The shared styled dialog cannot choose a fullscreen portal container. Use its
          Radix foundation here so Settings also covers the activity and sidebar. */}
      <DialogPrimitive.Portal container={portalContainer}>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-black/55" />
        <DialogPrimitive.Content
          ref={contentRef}
          data-testid="call-device-settings"
          aria-modal="true"
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            closeButtonRef.current?.focus()
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (returnFocusTarget?.isConnected) returnFocusTarget.focus({ preventScroll: true })
          }}
          onEscapeKeyDown={(event) => {
            // Radix handles Escape in the capture phase; let an open device picker
            // consume the first press before the dialog handles the next one.
            if (contentRef.current?.querySelector('[role="combobox"][aria-expanded="true"]')) event.preventDefault()
          }}
          className="fixed left-1/2 top-1/2 z-[81] flex h-[min(36rem,calc(100dvh-2rem))] w-[min(56rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-[#0f1419] text-white shadow-2xl ring-1 ring-white/10 focus:outline-none"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3 sm:px-6">
            <DialogPrimitive.Title className="text-lg font-semibold text-white">Settings</DialogPrimitive.Title>
            <button ref={closeButtonRef} type="button" aria-label="Close settings" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c]">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1">
            <div role="tablist" aria-label="Settings sections" aria-orientation="vertical" className="flex w-28 shrink-0 flex-col gap-1 overflow-y-auto border-r border-white/10 p-2 sm:w-44 sm:p-3">
              {panes.map((pane) => {
                const active = pane.id === activePane
                return (
                  <button
                    key={pane.id}
                    type="button"
                    role="tab"
                    id={`call-settings-tab-${pane.id}`}
                    aria-selected={active}
                    aria-controls={`call-settings-panel-${pane.id}`}
                    data-testid={`call-settings-tab-${pane.id}`}
                    onClick={() => setActivePane(pane.id)}
                    className={`flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-2.5 py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#f6a35c] ${active ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}
                  >
                    <span className={`hidden shrink-0 sm:inline-flex ${active ? "text-[#f6a35c]" : "text-white/50"}`}>{pane.icon}</span>
                    {pane.label}
                  </button>
                )
              })}
            </div>
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
              {panes.map((pane) => (
                <div key={pane.id} role="tabpanel" id={`call-settings-panel-${pane.id}`} aria-labelledby={`call-settings-tab-${pane.id}`} hidden={pane.id !== activePane}>
                  {content ? content[pane.id] : <>
                  {pane.id === "audio" && <DeviceSettingsMenu inline onMuteMicrophone={onMuteMicrophone} show={{ microphone: true, speaker: true, noise: true, refresh: true }} testId="call-settings-audio-devices" />}
                  {pane.id === "video" && (
                    <div className="space-y-4">
                      <DeviceSettingsMenu inline show={{ camera: true, refresh: true }} testId="call-settings-video-devices" />
                      <VideoQualitySettings />
                    </div>
                  )}
                  {pane.id === "general" && <GeneralPreferencesSettings />}
                  {pane.id === "effects" && <BackgroundEffects />}
                  {pane.id === "reactions" && <ReactionPreferencesSettings />}
                  </>}
                </div>
              ))}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
